import React, { useEffect, useState } from "react";
import { Breadcrumb, Layout, Card, Row, Col, Statistic, Spin, Table, Typography } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { FormattedMessage, useIntl } from "react-intl";
import { 
  BankOutlined, 
  TeamOutlined, 
  CalendarOutlined, 
  PictureOutlined, 
  ArrowUpOutlined
} from '@ant-design/icons';

const { Content } = Layout;
const { Title } = Typography;

// This would be fetched from the API in a real implementation
interface IDashboardStats {
  organisations: number;
  users: number;
  events: number;
  galleries: number;
  photos: number;
  newOrganisations: number;
  newUsers: number;
  newEvents: number;
  newPhotos: number;
}

interface IRecentActivity {
  id: string;
  type: string;
  name: string;
  organisation: string;
  timestamp: string;
}

const AdminDashboardPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<IDashboardStats>({
    organisations: 0,
    users: 0,
    events: 0,
    galleries: 0,
    photos: 0,
    newOrganisations: 0,
    newUsers: 0,
    newEvents: 0,
    newPhotos: 0
  });
  const [recentActivity, setRecentActivity] = useState<IRecentActivity[]>([]);

  useEffect(() => {
    // Simulate API fetch
    setTimeout(() => {
      setStats({
        organisations: 42,
        users: 156,
        events: 78,
        galleries: 214,
        photos: 8467,
        newOrganisations: 5,
        newUsers: 23,
        newEvents: 12,
        newPhotos: 347
      });
      
      setRecentActivity([
        { id: '1', type: 'Organisation', name: 'TechCorp', organisation: 'TechCorp', timestamp: '2023-04-12 14:30' },
        { id: '2', type: 'User', name: 'john.doe@example.com', organisation: 'TechCorp', timestamp: '2023-04-12 15:40' },
        { id: '3', type: 'Event', name: 'Annual Conference', organisation: 'EventPlanners', timestamp: '2023-04-11 09:15' },
        { id: '4', type: 'Gallery', name: 'VIP Sessions', organisation: 'EventPlanners', timestamp: '2023-04-11 11:20' },
        { id: '5', type: 'Organisation', name: 'Photography Inc', organisation: 'Photography Inc', timestamp: '2023-04-10 16:45' },
      ]);
      
      setLoading(false);
    }, 1000);
  }, []);

  // Table columns for recent activity
  const activityColumns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Organisation',
      dataIndex: 'organisation',
      key: 'organisation',
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
    },
  ];

  return (
    <Content style={{ margin: '0 16px', position: 'relative', overflow: 'hidden' }}>
      {/* DRAFT Watermark - contained within the content area */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%) rotate(-30deg)',
          pointerEvents: 'none',
          zIndex: 1,
          opacity: 0.15,
        }}
      >
        <Typography.Text
          style={{
            fontSize: '100px',
            fontWeight: 'bold',
            color: '#ff4d4f',
            whiteSpace: 'nowrap',
          }}
        >
          DRAFT
        </Typography.Text>
      </div>

      <Breadcrumb style={{ margin: '16px 0' }}>
        <Breadcrumb.Item>RecogniX</Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.adminDashboardName'} defaultMessage="Admin Dashboard" />
        </Breadcrumb.Item>
      </Breadcrumb>

      <div
        style={{
          padding: 24,
          minHeight: '79vh',
          background: 'white',
          borderRadius: 8,
          position: 'relative',
          zIndex: 0,
        }}
      >
        <Title level={2}>
          <FormattedMessage id={'admin.dashboard.title'} defaultMessage="System Overview" />
        </Title>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Loading dashboard data...</p>
          </div>
        ) : (
          <>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic 
                    title={intl.formatMessage({ id: 'admin.stats.organisations' }) || "Organisations"} 
                    value={stats.organisations} 
                    prefix={<BankOutlined />}
                    suffix={
                      <span style={{ fontSize: '14px', color: '#52c41a' }}>
                        <ArrowUpOutlined /> {stats.newOrganisations} new
                      </span>
                    }
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic 
                    title={intl.formatMessage({ id: 'admin.stats.users' }) || "Users"} 
                    value={stats.users} 
                    prefix={<TeamOutlined />} 
                    suffix={
                      <span style={{ fontSize: '14px', color: '#52c41a' }}>
                        <ArrowUpOutlined /> {stats.newUsers} new
                      </span>
                    }
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic 
                    title={intl.formatMessage({ id: 'admin.stats.events' }) || "Events"} 
                    value={stats.events} 
                    prefix={<CalendarOutlined />} 
                    suffix={
                      <span style={{ fontSize: '14px', color: '#52c41a' }}>
                        <ArrowUpOutlined /> {stats.newEvents} new
                      </span>
                    }
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic 
                    title={intl.formatMessage({ id: 'admin.stats.galleries' }) || "Galleries"} 
                    value={stats.galleries} 
                    prefix={<PictureOutlined />} 
                  />
                </Card>
              </Col>
            </Row>
            
            <Title level={3} style={{ marginTop: 32 }}>
              <FormattedMessage id={'admin.recentActivity'} defaultMessage="Recent Activity" />
            </Title>
            
            <Table 
              dataSource={recentActivity} 
              columns={activityColumns} 
              rowKey="id" 
              pagination={{ pageSize: 5 }}
            />
          </>
        )}
      </div>
    </Content>
  );
};

export default AdminDashboardPage;